import { createContext, useCallback, useContext, useMemo } from 'react';
import qs from 'qs';
import * as _ from 'lodash';
import type { FunctionComponent } from 'react';
import type { InjectedRouter, RouterState } from 'react-router';
import type { Params } from 'react-router/lib/Router';

type ProviderProps = {
  router: InjectedRouter & { params: Params};
  location: RouterState['location'];
};

export type RouterContextState = {
  getQuery: () => qs.ParsedQs;
  setQuery: <T extends object>(val: T) => void;
  getParams: <T extends object>() => T;
  getRouter: () => ProviderProps['router'];
  getLocation: () => ProviderProps['location'];
  replacePath: (path: string) => void;
};

export const RouterContext = createContext<RouterContextState>({} as RouterContextState);

/**
 * Similar to `useState`, but for storing and changing URL query parameters.
 * @param parserFn A function to convert any internal values to their proper types and provide defaults. When query params
 * are read from the URL, all primitive values are converted to strings. **IMPORTANT**: This *must* be wrapped in
 * a `useCallback` or defined outside of a component so that it's not recreated on rerender.
 *
 * The default parserFn is `_.identity`, which means that the query params are returned as they are.
 *
 *  * Note, this component uses the `qs` library under the hood.  That means it supports only the objects that that library
 * supports.  It supports records and arrays, but not functions or classes.  Note that it only supports objects nested
 * up to 5 levels deep.  It will also serialize dates, but not automatically deserialize them.
 *
 * @example
 * type MyState = {
 *  ids: number;
 * }
 *
 * const parserFn = useCallback((val) => {
 *  return {
 *    ids: (val.ids ?? []).map((id) => Number(id)),
 *  }
 * });
 *
 * const [queryState, setQueryState] = useQuery(parserFn);
 */
export const useQuery = <T extends object>(
  parserFn: (val: Record<string, any>) => T = _.identity,
): [T, (val: T) => void] => {
  const { getQuery, setQuery } = useContext(RouterContext);
  return useMemo(() => {
    const query = getQuery();

    const currentQuery = parserFn(query as T);
    return [currentQuery, setQuery];
  }, [getQuery, parserFn, setQuery]);
};

/**
 * Retrieves the URL parameters for the current page from the Router without needing to prop drill the router object to
 * every component.
 *
 * @example
 * // For the page http://localhost:9578/projects/custom%2B1%2Ftest-project/refs/branch/master/abcdefg
 *
 * type PageParams = {
 *   project_id: string;
 *   reference: string;
 *   reference_type: string;
 *   revision_id: string;
 * }
 *
 * const params = useParams<PageParams>();
 *
 * console.log(params);
 * // > { project_id: 'custom+1/test-project', reference_type: 'branch', reference: 'master', revision_id: 'abcdefg' }
 */
export const useParams = <T extends object>(): T => {
  const { getParams } = useContext(RouterContext);

  return getParams();
};

/**
 * Similar to `useQuery`, but returns the query as a raw object.
 */
export function useUnparsedQuery<T extends Record<string, any>>(): [T, (val: T) => void] {
  return useQuery<T>(_.identity);
}

export const useRouter = () => {
  const { getRouter } = useContext(RouterContext);

  return getRouter();
};

export const useLocation = () => {
  const { getLocation } = useContext(RouterContext);

  return getLocation();
};

/**
 * This context makes it easy to access the react-router Router from any component. It also provides utilities for
 * fetching and updating the URL query params.
 */
const RouterContextProvider: FunctionComponent<ProviderProps> = ({ children, router, location }) => {
  //  Handy way to get the router object from anywhere in the app.
  const getRouter = useCallback(() => router, [router]);

  // Handy way to get the location object from anywhere in the app.
  const getLocation = useCallback(() => location, [location]);

  //  Parse the query string from the URL.
  const getQuery = useMemo(() => (): qs.ParsedQs => {
    return qs.parse(location.search, { ignoreQueryPrefix: true });
  }, [location.search]);

  //  Set the query string in the URL.
  const setQuery = useMemo(() => (val: Record<string, any>) => {
    router.replace({
      pathname: location.pathname,
      search: `?${qs.stringify(val)}`,
    });
  }, [router, location.pathname]);

  //  Get the URL parameters for the current page.
  const getParams = useMemo(() => (): any => {
    return router.params;
  }, [router.params]);

  //  Replaces the current path with the provided path.
  const replacePath = useMemo(() => (pathname: string) => {
    router.replace({
      pathname,
    });
  }, [router]);

  const contextValue = useMemo(() => ({
    getQuery,
    setQuery,
    getParams,
    getRouter,
    getLocation,
    replacePath,
  }), [getQuery, setQuery, getParams, getRouter, getLocation, replacePath]);

  return (
    <RouterContext.Provider value={contextValue}>
      {children}
    </RouterContext.Provider>
  );
};

export default RouterContextProvider;
